import React, { useCallback, useEffect, useRef, useState } from "react";
import "./SidebarNavigation.css";
import { FiLogOut } from "react-icons/fi";
import CompanyLogo from "../../../assets/images/Futuresoft_logo.jpg";
import {
  BarChart3,
  Package,
  List,
  FileText,
  CreditCard,
  TrendingUp,
  ChevronRight,
  User,
  Settings,
  Bell,
  Search,
  Menu,
  HandCoins,
} from "lucide-react";
import { Modal } from "react-bootstrap";

import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  saveAuthData,
  saveStoreData,
} from "../../../features/login/LoginSlice";
import { FaSearch } from "react-icons/fa";
import { apiHandler } from "../../../api";
import usePermission from "../../../utils/usePermission";

const MIN_SEARCH_LENGTH = 3;
const DEBOUNCE_DELAY = 300;
const PAGE_SIZE = 100;

const CENTRAL_AUTH_URL = `https://ondcidentityfrontend.clouddeploy.in/login`;
const EXTERNAL_ISSUES_URL = "https://igmfront.clouddeploy.in/";
const TARGET_APP_NAME = "IGM Version 2";

const SidebarNavigation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const debounceTimer = useRef(null);
  const { authData, storeId, userDetails } = useSelector(
    (state) => state.loginSlice
  );
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [openSelectStore, setOpenSelectStore] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState(storeId);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [service, setService] = useState("RSF"); // This service setting is fixed

  const [totalCount, setTotalCount] = useState(0);
  const [stores, setStores] = useState({});

  const brandName =
    useSelector((state) => state.loginSlice.brandName) ||
    localStorage.getItem("brandName");

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const location = useLocation();

  // 2. Modified navigationItems: Added 'external' and 'externalUrl' properties to Issues
  const navigationItems = [
    {
      id: "analytics",
      label: "Analytics",
      icon: BarChart3,
      route: "/analytics",
    },
    {
      id: "payout",
      label: "Payout",
      icon: HandCoins,
      route: "/payout",
    },
    { id: "orders", label: "Orders", icon: Package, route: "/orders" },
    {
      id: "settlements",
      label: "Settlements",
      icon: CreditCard,
      route: "/settlements",
    },
    { id: "reports", label: "Reports", icon: TrendingUp, route: "/reports" },
    {
      id: "reconciliations",
      label: "Reconciliations",
      icon: TrendingUp,
      route: "/recons",
    },
    {
      id: "misc-settle",
      label: "MISC Settle",
      icon: CreditCard,
      route: "/misc-settlements",
    },
    {
      id: "issues",
      label: "Issues",
      icon: Bell,
      // route: "/issues", // Remove internal route
      external: true, // Mark as external
      externalUrl: EXTERNAL_ISSUES_URL, // Use the defined external URL
    },
    { id: "settings", label: "Settings", icon: Settings, route: "/settings" },
    { id: "contact-us", label: "Contact Us", icon: User, route: "/contact-us" },
    { id: "faq", label: "FAQ", icon: FileText, route: "/faq" },
    {
      id: "help-center",
      label: "Help Center",
      icon: Search,
      route: "/help-center",
    },
  ];

  const isTabActive = (itemPath) => {
    // Only works for internal routes, not external ones
    if (itemPath === "/") {
      return location.pathname === "/";
    }
    return location?.pathname?.startsWith(itemPath) ?? false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    navigate("/");
    dispatch(saveAuthData(false));
  };
  const userData = useSelector((state) => state.loginSlice);

  const isAdmin = userData?.isAdmin; // Returns true or false
  const canViewIgm = usePermission("IGM_VIEW");

  // let canRead = userDetails?.includes("MULTI_STORES"); // Note: This line is not used in the final logic you requested

  const allStores = [];
  if (stores && typeof stores === "object") {
    Object.values(stores).forEach((storeList) => {
      if (Array.isArray(storeList)) {
        allStores.push(...storeList);
      }
    });
  }

  const fetchStores = useCallback(
    async (searchQuery = "") => {
      // ... (Your existing fetchStores logic) ...
      // [Keeping the existing implementation of fetchStores for brevity]
      try {
        setLoading(true);
        setError(null);

        const response = await apiHandler({
          url: `https://ondcidentitybackend.clouddeploy.in/api/v1/tenant/getServiceTenants?iPageSize=${PAGE_SIZE}&sSearchByName=${encodeURIComponent(
            searchQuery
          )}&sService=${encodeURIComponent(service)}`,
          method: "GET",
          authToken: authData,
          dispatch,
          storeId,
        });

        if (response?.data?.statusCode === 200) {
          const groupedStoreData = response?.data?.data || {};
          setStores(groupedStoreData);
          setError(null);
          setTotalCount(response?.data?.totalRecords || 0);
        } else {
          setStores({});
          setError(response?.data?.message);
        }
      } catch (err) {
        setStores({});
        setError(err.message || "Failed to fetch stores");
      } finally {
        setLoading(false);
      }
    },
    [authData, dispatch, storeId, service]
  );

  const handleStoreSearch = (e) => {
    const searchQuery = e.target.value;
    setSearch(searchQuery);
    debouncedSearch(searchQuery);
  };

  const debouncedSearch = useCallback(
    (searchQuery) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      if (searchQuery === "" || searchQuery.length >= MIN_SEARCH_LENGTH) {
        debounceTimer.current = setTimeout(() => {
          fetchStores(searchQuery);
        }, DEBOUNCE_DELAY);
      }
    },
    [fetchStores]
  );

  useEffect(() => {
    fetchStores(search);
  }, [search, fetchStores]);

  const handleChangeStore = (store) => {
    setSelectedStoreId(store.id);
    dispatch(
      saveStoreData({
        storeId: store.id,
        brandName: store.brand_name,
      })
    );
    setOpenSelectStore(false);
    navigate("/");
  };

  const handleExternalNavigation = (item) => {
    if (item.external && item.externalUrl) {
      // --- Scenario 1: User is NOT authenticated ---
      if (!authData?.accessToken) {
        const redirectUrl = window.location.origin + window.location.pathname;
        const applicationName = TARGET_APP_NAME; // "IGM Version 2"

        // Redirect to Central Auth for login
        window.location.href = `${CENTRAL_AUTH_URL}?redirectUrl=${encodeURIComponent(
          redirectUrl
        )}&appName=${encodeURIComponent(applicationName)}`;

        return; // Stop execution
      }

      // --- Scenario 2: User IS authenticated ---

      // Prepare data as JSON strings for URL encoding
      const authString = JSON.stringify(authData);
      const authorizationString = JSON.stringify(
        userDetails?.permissions || []
      );
      const isAdminString = userDetails?.isAdmin ? "true" : "false";
      const storeIdValue = storeId || "";

      // Create and encode the query string with all necessary session data
      const params = new URLSearchParams({
        auth: authString,
        authorization: authorizationString,
        isAdmin: isAdminString,
        storeId: storeIdValue,
        appName: TARGET_APP_NAME,
      }).toString();

      // Perform the final redirection to the Issues application (e.g., https://igm.clouddeploy.in/)
      const finalRedirectUrl = `${item.externalUrl}?${params}`;
      window.open(finalRedirectUrl, '_blank');
    }
  };

  return (
    <div className="sidebar-container">
      <div className={`sidebar ${sidebarCollapsed ? "collapsed" : ""}`}>
        <div className="sidebar-header">
          {/* ... (Existing header content) ... */}
          <div className="logo-section">
            {!sidebarCollapsed && (
              <div className="logo-text">
                <div className="brand-name">
                  <img className="OndcLogo" src={CompanyLogo} alt="Logo" />
                </div>
              </div>
            )}
          </div>
          <button
            className="sidebar-toggle"
            onClick={toggleSidebar}
            title={sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            <Menu />
          </button>
        </div>
        <nav className="nav-section">
          {navigationItems.map((item) => {
            const isIssuesTab = item.id === "issues";
            const shouldShow = !isIssuesTab || !canViewIgm;

            if (!shouldShow) {
              return null;
            }

            const isDisabled = item.disabled;

            return (
              <div key={item.id} className="nav-item">
                {isDisabled ? (
                  <div className="nav-link disabled" title="Coming Soon">
                    <div className="nav-content">
                      <item.icon className="nav-icon text-muted" />
                      {!sidebarCollapsed && (
                        <span className="nav-text text-muted">
                          {item.label}
                        </span>
                      )}
                    </div>
                  </div>
                ) : item.external ? (
                  <div
                    className="nav-link"
                    onClick={() => handleExternalNavigation(item)}
                    style={{ cursor: "pointer" }}
                    title={sidebarCollapsed ? item.label : ""}
                  >
                    <div className="nav-content">
                      <item.icon className="nav-icon" />
                      {!sidebarCollapsed && (
                        <span className="nav-text">{item.label}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <Link
                    to={item.route}
                    className={`nav-link ${
                      isTabActive(item.route) ? "active" : ""
                    }`}
                    title={sidebarCollapsed ? item.label : ""}
                  >
                    <div className="nav-content">
                      <item.icon className="nav-icon" />
                      {!sidebarCollapsed && (
                        <span className="nav-text">{item.label}</span>
                      )}
                    </div>
                    {isTabActive(item.route) && (
                      <div className="active-indicator" />
                    )}
                  </Link>
                )}
              </div>
            );
          })}
        </nav>

        {/* ... (Store and Logout Sections) ... */}
        <div className="d-flex justify-content-left p-3">
          {isAdmin && (
            <div className="store-container">
              <div className="active-store">
                <span className="label">Active Store:</span>
                <span className="name">{brandName || "No store selected"}</span>
              </div>
              <button
                onClick={() => setOpenSelectStore(true)}
                className="btn btn-primary btn-sm px-4 py-2 text-white font-weight-bold rounded-3 shadow-sm hover:shadow-lg transition-all"
                style={{ fontSize: "0.875rem" }}
              >
                Change Store
              </button>
            </div>
          )}
        </div>

        <div className="user-section">
          <span
            style={{ cursor: "pointer" }}
            className="logout-button"
            onClick={handleSubmit}
          >
            <FiLogOut size={18} style={{ marginRight: "8px" }} />
            {!sidebarCollapsed && "Logout"}
          </span>
        </div>
      </div>

      {/* ... (Modal Component - No changes needed here) ... */}
      <Modal
        centered
        show={openSelectStore}
        onHide={() => setOpenSelectStore(false)}
      >
        <Modal.Header closeButton>Change Store</Modal.Header>
        <Modal.Body>
          <div className="store-selector-card">
            {/* Search Section */}
            <div className="search-section">
              <div className="search-input-group">
                <input
                  type="search"
                  name="search"
                  id="storeSearch"
                  value={search}
                  onChange={handleStoreSearch}
                  className="form-control search-input"
                  placeholder="Search tenants by name..."
                />
              </div>
            </div>

            {/* Store List */}
            <div
              className="accordion-body p-2"
              style={{ maxHeight: "400px", overflowY: "auto" }}
            >
              {loading ? (
                <div className="text-muted text-center py-3">Loading...</div>
              ) : error ? (
                <div className="text-danger text-center py-3">{error}</div>
              ) : allStores.length > 0 ? (
                allStores.map((store) => (
                  <div
                    key={store.id}
                    className={`card mb-2 border-0 shadow-sm ${
                      selectedStoreId === store.id
                        ? "border-primary bg-info bg-opacity-25"
                        : ""
                    }`}
                    onClick={() => handleChangeStore(store)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="card-body d-flex align-items-center p-2">
                      <img
                        src={store.brand_logo}
                        alt={store.brand_name}
                        className="me-3 rounded border"
                        style={{
                          width: "40px",
                          height: "40px",
                          objectFit: "cover",
                        }}
                        onError={(e) => {
                          e.target.src = "https://via.placeholder.com/60";
                        }}
                      />
                      <div>
                        <h6 className="mb-0 text-truncate">
                          {store.brand_name}{" "}
                          {store.type ? `(${store.type})` : ""}
                        </h6>
                        <small className="text-muted">ID: {store.id}</small>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-muted text-center py-3">
                  No stores available.
                </div>
              )}
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default SidebarNavigation;
